import type { V2 } from '../core/math';
import type { Witnesses } from './heatModel';

/**
 * Who saw it? Pure: positions in, counts out. Line of sight is injected (the heat system passes a
 * physics raycast; tests pass a fake), so the rule itself is unit-testable.
 */
export interface Observer {
  x: number;
  z: number;
  kind: 'police' | 'civilian';
  /** How far this observer notices things (m). */
  range: number;
}

export type LineOfSight = (from: V2, to: V2) => boolean;

/** Max line-of-sight checks spent on civilians per crime (closest first); police are always checked. */
export const MAX_CIVILIAN_CHECKS = 8;

export function countWitnesses(pos: V2, observers: readonly Observer[], los: LineOfSight, maxCivilianChecks = MAX_CIVILIAN_CHECKS): Witnesses {
  const w: Witnesses = { police: 0, civilians: 0 };
  const civ: { o: Observer; d2: number }[] = [];
  for (const o of observers) {
    const dx = o.x - pos.x;
    const dz = o.z - pos.z;
    const d2 = dx * dx + dz * dz;
    if (d2 > o.range * o.range) continue;
    if (o.kind === 'police') {
      if (los({ x: o.x, z: o.z }, pos)) w.police++;
    } else {
      civ.push({ o, d2 });
    }
  }
  civ.sort((a, b) => a.d2 - b.d2);
  const n = Math.min(civ.length, maxCivilianChecks);
  for (let i = 0; i < n; i++) {
    const o = civ[i]!.o;
    if (los({ x: o.x, z: o.z }, pos)) w.civilians++;
  }
  return w;
}

/**
 * Recognition range for an officer looking for the suspect: full range if the description fits,
 * a few metres if the player changed vehicle out of sight (they'd have to look you in the face),
 * shorter still while hiding in cover.
 */
export function recognitionRange(baseRange: number, matchesDescription: boolean, hidden: boolean, inAlley: boolean): number {
  let r = matchesDescription ? baseRange : 9;
  if (inAlley) r *= 0.6;
  if (hidden) r = Math.min(r, 7);
  return r;
}
